import React from 'react';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';

const OdcSalesContent = ({
    allData,
    selectedCompany,
    setSelectedCompany,
    filteredCategories,
    selectedCategory,
    setSelectedCategory,
    selectedProduct,
    setSelectedProduct,
    filteredProducts,
    cartItems,
    setCartItems,
    onAddToCart,
    customerName, setCustomerName,
    customerPhone, setCustomerPhone,
    customerGst, setCustomerGst,
    orderDate, setOrderDate,
    orderTime, setOrderTime,
    deliveryDate, setDeliveryDate,
    deliveryTime, setDeliveryTime,
    orderTakenBy, setOrderTakenBy,
    remarks, setRemarks,
    selectedPaymentMode, setSelectedPaymentMode,
    totalSummary,
    loading,
    onSubmit
}) => {
    const isWalkIn = selectedCompany?.name === 'Walk-In';

    return (
        <>
            {/* Product Selection Card */}
            <Card className="mb-4 shadow-3 border-none p-2">
                <div className="p-fluid grid formgrid align-items-end mb-4 px-2">
                    <div className="field col-12 md:col-3">
                        <label className="text-sm font-bold text-700 uppercase mb-2 block">Order Type</label>
                        {cartItems.length > 0 && selectedCompany ? (
                            <span className="font-bold text-800 text-lg">{selectedCompany.name}</span>
                        ) : (
                            <Dropdown
                                value={selectedCompany}
                                options={allData.companies}
                                onChange={(e) => setSelectedCompany(e.value)}
                                optionLabel="name"
                                placeholder="Choose Order Type"
                                className="p-inputtext-sm shadow-sm"
                            />
                        )}
                    </div>
                    <div className="field col-12 md:col-3">
                        <label className="text-sm font-bold text-700 uppercase mb-2 block">Category</label>
                        <Dropdown
                            value={selectedCategory}
                            options={filteredCategories}
                            onChange={(e) => setSelectedCategory(e.value)}
                            optionLabel="name"
                            placeholder="Choose Category"
                            disabled={!selectedCompany}
                            className="p-inputtext-sm shadow-sm"
                            filter
                        />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label className="text-sm font-bold text-700 uppercase mb-2 block">Select Product</label>
                        <Dropdown
                            value={selectedProduct}
                            options={filteredProducts}
                            onChange={(e) => setSelectedProduct(e.value)}
                            optionLabel="product_name"
                            placeholder="Search and Select..."
                            disabled={!selectedCategory}
                            className="p-inputtext-sm shadow-sm"
                            filter
                        />
                    </div>
                    <div className="field col-12 md:col-2">
                        <Button
                            label="Add Item"
                            icon="pi pi-plus"
                            className="p-button-sm p-button-primary font-bold shadow-2"
                            onClick={onAddToCart}
                            disabled={!selectedProduct}
                        />
                    </div>
                </div>

                <DataTable
                    value={cartItems}
                    dataKey="uniqueId"
                    className="p-datatable-sm mt-2"
                    scrollable
                    scrollHeight="400px"
                    size="small"
                    stripedRows
                    showGridlines
                    responsiveLayout="scroll"
                >
                    <Column field="name" header="PRODUCT DESCRIPTION" className="font-bold text-800" style={{ minWidth: '250px' }} />
                    <Column header="UNIT RATE" body={(r) => <span className="font-semibold">₹{r.basic_rate.toFixed(2)}</span>} />
                    <Column
                        header="QUANTITY"
                        body={(r) => (
                            <InputNumber
                                value={r.quantity}
                                onValueChange={(e) =>
                                    setCartItems((prev) => prev.map((item) => (item.uniqueId === r.uniqueId ? { ...item, quantity: e.value } : item)))
                                }
                                showButtons
                                min={0}
                                maxFractionDigits={3}
                                inputClassName="w-6rem p-inputtext-sm text-center font-bold"
                            />
                        )}
                    />
                    <Column header="TAX %" body={(r) => <span className="text-600 font-medium">{r.tax}%</span>} />
                    <Column
                        header="TOTAL AMOUNT"
                        body={(r) => <span className="font-bold text-blue-700">₹{(r.basic_rate * (r.quantity || 0) * (1 + r.tax / 100)).toFixed(2)}</span>}
                    />
                    <Column
                        style={{ width: '4rem' }}
                        body={(r) => (
                            <Button
                                icon="pi pi-trash"
                                className="p-button-danger p-button-text p-button-rounded"
                                onClick={() => setCartItems(cartItems.filter((i) => i.uniqueId !== r.uniqueId))}
                            />
                        )}
                    />
                </DataTable>
            </Card>

            {/* Order Details + Billing Summary */}
            <div className="grid grid-nogutter align-items-stretch">
                <div className="col-12 lg:col-8 pr-0 lg:pr-4">
                    <Card className="shadow-3 border-none p-3 h-full">
                        <div className="flex align-items-center mb-4 text-primary border-bottom-1 pb-2 border-200">
                            <i className="pi pi-truck mr-2 font-bold text-xl" />
                            <h6 className="m-0 text-sm font-bold uppercase tracking-wider">Order & Delivery Details</h6>
                        </div>

                        <div className="p-fluid grid formgrid">
                            <div className="field col-12 md:col-4 px-2">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Customer Name</label>
                                <InputText value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Full Name" className="p-inputtext-sm" />
                            </div>
                            <div className="field col-12 md:col-4 px-2">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Phone Number</label>
                                <InputText value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="Mobile No." className="p-inputtext-sm" />
                            </div>
                            <div className="field col-12 md:col-4 px-2">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">GST Number</label>
                                <InputText value={customerGst} onChange={(e) => setCustomerGst(e.target.value)} placeholder="Optional" className="p-inputtext-sm" />
                            </div>

                            <div className="field col-12 md:col-6 px-2">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Order Date</label>
                                <Calendar value={orderDate} onChange={(e) => setOrderDate(e.value)} dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                            </div>
                            <div className="field col-12 md:col-6 px-2 mb-4">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Order Time</label>
                                <Calendar value={orderTime} onChange={(e) => setOrderTime(e.value)} timeOnly hourFormat="12" showIcon className="p-inputtext-sm" />
                            </div>

                            <div className="field col-12 md:col-6 px-2">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Exp. Delivery Date</label>
                                <Calendar value={deliveryDate} onChange={(e) => setDeliveryDate(e.value)} dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                            </div>
                            <div className="field col-12 md:col-6 px-2 mb-4">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Exp. Delivery Time</label>
                                <Calendar value={deliveryTime} onChange={(e) => setDeliveryTime(e.value)} timeOnly hourFormat="12" showIcon className="p-inputtext-sm" />
                            </div>

                            <div className="field col-12 md:col-6 px-2">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Order Taken By</label>
                                <InputText value={orderTakenBy} onChange={(e) => setOrderTakenBy(e.target.value)} placeholder="User Name" className="p-inputtext-sm" />
                            </div>
                            <div className="field col-12 md:col-6 px-2">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Special Remarks</label>
                                <InputText value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Instructions..." className="p-inputtext-sm" />
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-12 lg:col-4 mt-4 lg:mt-0">
                    <Card className="shadow-3 border-none p-4 bg-white h-full border-top-3 border-primary flex flex-column">
                        <div className="flex align-items-center mb-4 text-700 border-bottom-1 pb-2 border-200">
                            <i className="pi pi-receipt mr-2 font-bold text-xl" />
                            <h6 className="m-0 text-sm font-bold uppercase tracking-wider">Billing Summary</h6>
                        </div>

                        <div className="flex justify-content-between mb-2">
                            <span className="text-700 font-semibold">Subtotal:</span>
                            <span className="font-bold text-800">₹{totalSummary.subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-content-between mb-3">
                            <span className="text-700 font-semibold">Total Tax:</span>
                            <span className="text-orange-600 font-bold">+ ₹{totalSummary.tax.toFixed(2)}</span>
                        </div>

                        <Divider className="my-3" />

                        <div className="flex justify-content-between align-items-center mb-4">
                            <span className="text-lg font-bold text-900">GRAND TOTAL:</span>
                            <span className="text-3xl font-black text-primary">₹{(totalSummary.subtotal + totalSummary.tax).toFixed(2)}</span>
                        </div>

                        <Divider className="my-3" />

                        {/* Payment Mode — Walk-In: select from settings; ODC: auto credit */}
                        {isWalkIn ? (
                            <div className="mb-4">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">
                                    <i className="pi pi-wallet mr-1 text-primary" />
                                    Payment Mode
                                </label>
                                <Dropdown
                                    value={selectedPaymentMode}
                                    options={allData.settings}
                                    onChange={(e) => setSelectedPaymentMode(e.value)}
                                    optionLabel="name"
                                    placeholder="Select Payment Mode (optional)"
                                    className="w-full p-inputtext-sm"
                                />
                                <small className="text-500 mt-1 block">
                                    {selectedPaymentMode ? `MOP: ${selectedPaymentMode.name}` : 'Defaults to CREDIT if not selected'}
                                </small>
                            </div>
                        ) : selectedCompany && (
                            <div className="mb-4 flex align-items-center justify-content-between surface-100 border-round p-3">
                                <span className="text-sm font-bold text-700 uppercase">Payment Mode</span>
                                <span className="text-sm font-bold text-orange-600 border-1 border-orange-300 px-2 py-1 border-round">
                                    <i className="pi pi-clock mr-1" />CREDIT
                                </span>
                            </div>
                        )}

                        <div className="mt-auto">
                            <Button
                                label="PLACE ORDER"
                                icon="pi pi-check-circle"
                                loading={loading}
                                className="p-button-success w-full py-3 font-bold shadow-2 text-lg"
                                onClick={() => confirmDialog({ message: 'Confirm and place this order?', header: 'Confirmation', accept: onSubmit })}
                                disabled={!cartItems.length || !orderTakenBy || loading}
                            />
                        </div>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default OdcSalesContent;
